import { useCallback, useEffect, useState } from "react";

import { useAccess } from "./lib/session";
import { supabase } from "./lib/supabase";
import { loadEmployer, type EmployerView } from "./lib/employer";
import { Employees } from "./routes/Employees";
import { NewPassword } from "./routes/NewPassword";
import { Decisions } from "./routes/Decisions";
import { Payslip } from "./routes/Payslip";
import { Person } from "./routes/Person";
import { Setup } from "./routes/Setup";
import { Register } from "./routes/Register";
import { SignIn } from "./routes/SignIn";

type Tab = "people" | "register" | "payslip" | "decisions" | "setup";

export function App() {
  const access = useAccess();

  if (access.state === "loading") {
    return <div className="centered">Checking your session…</div>;
  }

  if (access.state === "signed-out") {
    return <SignIn problem={access.problem} />;
  }

  // A reset link got them here. Nothing else opens until the password is set.
  if (access.state === "recovery") {
    return <NewPassword />;
  }

  // Signed in, but we could not establish what they may see. Say so, rather
  // than bouncing them back to a sign-in form they just used successfully.
  if (access.state === "error") {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <a className="logo" href="https://shiftygroup.com">
            shifty<span>.</span>
          </a>
          <h1>Signed in, but stuck</h1>
          <p className="sub">
            You are signed in, but we could not read which household you manage.
          </p>
          <div className="notice error">{access.message}</div>
          <button className="btn" type="button" onClick={() => supabase.auth.signOut()}>
            Sign out and try again
          </button>
          <p className="auth-foot">
            If this keeps happening, send that message to{" "}
            <a href="mailto:info@shiftygroup.com">info@shiftygroup.com</a>.
          </p>
        </div>
      </div>
    );
  }

  // A worker who tries the dashboard has not done anything wrong. Say what this
  // is, point them at the app, and let them out.
  if (access.state === "not-an-employer") {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <a className="logo" href="https://shiftygroup.com">
            shifty<span>.</span>
          </a>
          <h1>This page is for employers</h1>
          <p className="sub">
            {access.email ?? "That account"} is not set up to manage a household. If you are
            employed through Shifty, everything you need is in the app on your phone — your
            shifts, your leave balance and your documents.
          </p>
          <button className="btn" type="button" onClick={() => supabase.auth.signOut()}>
            Sign out
          </button>
          <p className="auth-foot">
            If you think this is wrong, ask your employer to check your access, or write to{" "}
            <a href="mailto:info@shiftygroup.com">info@shiftygroup.com</a>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <Dashboard
      orgId={access.orgs[0]!.id}
      orgCount={access.orgs.length}
      canApprove={access.orgs[0]!.canApprove}
    />
  );
}

function Dashboard({ orgId, orgCount, canApprove }: {
  orgId: string;
  orgCount: number;
  /** Whether this account may answer leave and overtime. Read from the
   *  membership rather than assumed from being able to see the household —
   *  a manager without it can look and cannot decide. */
  canApprove: boolean;
}) {
  const [ctx, setCtx] = useState<EmployerView | null | "loading">("loading");
  const [tab, setTab] = useState<Tab>("people");
  // Not a tab: one person's file is somewhere you go from People, and
  // putting it in the nav would mean picking somebody arbitrary to show.
  const [personId, setPersonId] = useState<string | null>(null);

  // Loaded once here rather than per screen: People and the register describe
  // the same household, and fetching it twice would let them disagree.
  const refresh = useCallback(async () => {
    setCtx(await loadEmployer(orgId));
  }, [orgId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <>
      <header className="app-head">
        <div className="inner">
          <span className="logo">
            shifty<span>.</span>
          </span>
          <nav className="tabs">
            <button
              type="button"
              aria-current={tab === "people" ? "page" : undefined}
              onClick={() => { setTab("people"); setPersonId(null); }}
            >
              People
            </button>
            <button
              type="button"
              aria-current={tab === "register" ? "page" : undefined}
              onClick={() => { setTab("register"); setPersonId(null); }}
            >
              Register
            </button>
            <button
              type="button"
              aria-current={tab === "payslip" ? "page" : undefined}
              onClick={() => { setTab("payslip"); setPersonId(null); }}
            >
              Payslip
            </button>
            <button
              type="button"
              aria-current={tab === "decisions" ? "page" : undefined}
              onClick={() => { setTab("decisions"); setPersonId(null); }}
            >
              Waiting on you
            </button>
            <button
              type="button"
              aria-current={tab === "setup" ? "page" : undefined}
              onClick={() => { setTab("setup"); setPersonId(null); }}
            >
              Setup
            </button>
          </nav>
          <span className="org">
            {ctx !== "loading" && ctx ? ctx.orgName : ""}
            {orgCount > 1 ? ` and ${orgCount - 1} more` : ""}
          </span>
          <button
            className="btn quiet"
            style={{ width: "auto" }}
            onClick={() => supabase.auth.signOut()}
          >
            Sign out
          </button>
        </div>
      </header>
      <main className="main">
        {ctx === "loading" ? (
          <div className="centered">Loading your household…</div>
        ) : !ctx ? (
          <div className="centered">
            We could not load this household. Refresh, or write to info@shiftygroup.com.
          </div>
        ) : personId ? (
          <Person ctx={ctx} employeeId={personId} onBack={() => setPersonId(null)} />
        ) : tab === "people" ? (
          <Employees ctx={ctx} onChanged={refresh} onOpen={setPersonId} />
        ) : tab === "payslip" ? (
          <Payslip ctx={ctx} />
        ) : tab === "decisions" ? (
          <Decisions ctx={ctx} canApprove={canApprove} />
        ) : tab === "setup" ? (
          <Setup ctx={ctx} canApprove={canApprove} />
        ) : (
          <Register ctx={ctx} />
        )}
      </main>
    </>
  );
}
